'use client';

import AutoSliderNeupos from './AutoSliderNeupos';
import Link from 'next/link';
import NeuposHero from './NeuposHero';
import PricingSection from './PricingSection';
import ServicesHero from './ServicesHero';
import { motion } from 'framer-motion';

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

const staggerContainer = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.08 },
    },
};

/* ---------------- SECTION HEADING ---------------- */

const SectionHeading = ({ eyebrow, heading, description, center }) => (
    <div
        className={`mb-8 md:mb-10 ${
            center ? 'text-center mx-auto' : ''
        } max-w-2xl`}
    >
        {eyebrow && (
            <p className="text-blue-500 font-bold text-sm mb-3">{eyebrow}</p>
        )}
        {heading && (
            <h2 className="text-2xl md:text-4xl font-bold text-gray-800 leading-tight">
                {heading}
            </h2>
        )}
        {description && (
            <p className="mt-4 text-gray-600 text-base md:text-lg">
                {description}
            </p>
        )}
    </div>
);

/* ---------------- CHECK ITEM GRID ---------------- */

const ItemGrid = ({ items, dense }) => (
    <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className={`grid grid-cols-1 sm:grid-cols-2 ${
            dense ? '' : 'lg:grid-cols-3'
        } gap-3 md:gap-4`}
    >
        {items.map((item, i) => (
            <motion.div
                key={i}
                variants={fadeUp}
                className={
                    dense
                        ? 'flex items-start gap-2.5'
                        : 'flex items-start gap-3 bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-300'
                }
            >
                <i className="ri-checkbox-circle-fill text-blue-500 text-lg mt-0.5 shrink-0"></i>
                <span className="text-gray-700 text-sm font-medium">
                    {item}
                </span>
            </motion.div>
        ))}
    </motion.div>
);

/* ---------------- FLOW CHIPS ---------------- */

const FlowList = ({ items }) => (
    <div className="flex flex-col gap-3">
        {items.map((item, i) => (
            <span
                key={i}
                className="inline-block w-fit bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-2.5 rounded-full"
            >
                {item}
            </span>
        ))}
    </div>
);

/* ---------------- SECTION ---------------- */

const Section = ({ section, tone }) => {
    const {
        eyebrow,
        heading,
        description,
        layout = 'grid',
        items,
        groups,
        paragraphs,
    } = section;

    return (
        <section
            className={`w-full py-10 md:py-14 px-4 ${
                tone === 'tint' ? 'bg-blue-50/40' : 'bg-white'
            }`}
        >
            <motion.div
                className="max-w-6xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
            >
                <SectionHeading
                    eyebrow={eyebrow}
                    heading={heading}
                    description={description}
                />

                {layout === 'grid' && items && <ItemGrid items={items} />}

                {layout === 'flow' && items && <FlowList items={items} />}

                {layout === 'text' && paragraphs && (
                    <div className="space-y-4 max-w-3xl">
                        {paragraphs.map((p, i) => (
                            <p
                                key={i}
                                className="text-gray-600 text-base leading-relaxed"
                            >
                                {p}
                            </p>
                        ))}
                    </div>
                )}

                {layout === 'groups' && groups && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {groups.map((group, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.05,
                                }}
                                className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 md:p-7"
                            >
                                <h3 className="font-bold text-gray-800 text-lg mb-2">
                                    {group.heading}
                                </h3>
                                {group.description && (
                                    <p className="text-gray-600 text-sm mb-4">
                                        {group.description}
                                    </p>
                                )}
                                {group.items && (
                                    <ItemGrid items={group.items} dense />
                                )}
                            </motion.div>
                        ))}
                    </div>
                )}
            </motion.div>
        </section>
    );
};

/* ---------------- MAIN TEMPLATE ---------------- */

export default function ServiceDetailTemplate({ data }) {
    const {
        hero,
        heroImages,
        intro,
        slider,
        sections = [],
        pricing,
        idealFor,
        highlight,
        cta,
    } = data;
    const callout =
        highlight ?? (idealFor ? { label: 'Ideal For', text: idealFor } : null);

    return (
        <div className="bg-white">
            {heroImages ? (
                <NeuposHero
                    heading={hero.subtitle}
                    caption={hero.caption}
                    buttonText={hero.buttonText}
                    buttonLink="/contact-sales"
                    leftImage={heroImages.left}
                    centerImage={heroImages.center}
                    rightImage={heroImages.right}
                    showShadowOnImage
                />
            ) : (
                <ServicesHero data={{ maintext: hero }} />
            )}

            {intro && (
                <section className="w-full pt-2 pb-6 md:pb-10 px-4">
                    <motion.div
                        className="max-w-3xl mx-auto text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                        {intro.eyebrow && (
                            <p className="text-blue-500 font-bold text-sm mb-3">
                                {intro.eyebrow}
                            </p>
                        )}
                        {intro.heading && (
                            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                                {intro.heading}
                            </h2>
                        )}
                        {intro.description && (
                            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                                {intro.description}
                            </p>
                        )}
                    </motion.div>
                </section>
            )}

            {slider && (
                <motion.section
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="py-12 md:py-20"
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-center text-gray-900">
                            {slider.title}
                        </h2>

                        <p className="text-center text-gray-600 mt-3 mb-12 max-w-3xl mx-auto">
                            {slider.caption}
                        </p>

                        <AutoSliderNeupos
                            images={slider.images}
                            speed={slider.sliderSettings?.speed}
                            direction={slider.sliderSettings?.direction}
                            gradient={slider.sliderSettings?.gradient}
                            gradientWidth={slider.sliderSettings?.gradientWidth}
                        />
                    </div>
                </motion.section>
            )}

            {sections.map((section, i) => (
                <Section
                    key={i}
                    section={section}
                    tone={i % 2 === 1 ? 'tint' : 'white'}
                />
            ))}

            {pricing && <PricingSection pricing={pricing} />}

            {callout && (
                <section className="w-full pb-12 md:pb-16 px-4">
                    <motion.div
                        className="max-w-4xl mx-auto bg-blue-50 border-l-4 border-blue-500 rounded-r-2xl p-6 md:p-8"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                        <p className="text-blue-600 font-bold text-xs uppercase tracking-wide mb-2">
                            {callout.label}
                        </p>
                        <p className="text-gray-700 font-medium">
                            {callout.text}
                        </p>
                    </motion.div>
                </section>
            )}

            <section className="w-full pb-16 md:pb-20 px-4">
                <motion.div
                    className="max-w-3xl mx-auto text-center bg-gray-50 rounded-3xl p-8 md:p-12"
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                        {cta?.heading ?? 'Ready to get started?'}
                    </h2>
                    <p className="mt-3 text-gray-600 text-base md:text-lg">
                        {cta?.description ??
                            'Talk to our team and find the right fit for your business.'}
                    </p>
                    <Link href="/contact-sales">
                        <button className="hello_button py-3 px-10 bg-blue-500 rounded-md mt-6 shadow-xl hover:shadow-none transition duration-300">
                            <span className="font-bold text-white text-sm md:text-base">
                                {cta?.buttonText ?? 'Talk to an Expert'}
                            </span>
                        </button>
                    </Link>
                </motion.div>
            </section>
        </div>
    );
}
